import React, { useContext, useEffect, useMemo, useState } from 'react';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import { JyotishCompat } from '../Edu/JyotishCompat';
import { JyotishGenderToggle } from '../Edu/JyotishGenderToggle';
import { JYOTISH_SUMMARY_SECTIONS, JyotishSummary } from '../Edu/JyotishSummary';
import { getJyotish } from '../Edu/utilsJyotish';
import { Avatar } from '../UiKit/Avatar';
import { AvatarName } from '../UiKit/AvatarName';
import { ensureAvatarCity } from '../utilsGeo';

const PHS_ITEM_CLASS = 'x_phs_item1 x_phs_item1--100 x_edu_penta_oneperson';
const COMPAT_SECTIONS = ['yoni', 'varna', 'gana'];

const normalizeGender = (gender) => {
  if (!gender) return null;
  const g = String(gender).toLowerCase();
  if (g === 'm' || g === 'male' || g === 'man') return 'm';
  if (g === 'f' || g === 'female' || g === 'woman') return 'f';
  return null;
};

const oppositeGender = (gender) => (gender === 'm' ? 'f' : gender === 'f' ? 'm' : null);

const getMemberKundli = async (avatar) => {
  const hasBirthData = Boolean(avatar?.design?.meta?.personality || avatar?.dob);
  if (!hasBirthData) return null;
  try {
    const avatarWithCity = await ensureAvatarCity(avatar);
    if (!avatarWithCity?.aggr?.city?.location) return null;
    return getJyotish(avatarWithCity)?.kundli || null;
  } catch {
    return null;
  }
};

export const PentaJyotish = React.memo((props) => {
  const { penta } = { ...props };

  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $ui = context.$ui;

  const compareColumnWidth = `${(100 / penta.members.length)}%`;
  const [kundlis, setKundlis] = useState({});
  const [genders, setGenders] = useState({});
  const showCompat = penta.members.length === 2;

  const memberTokens = useMemo(
    () => penta.members.map((m) => m.avatar.token),
    [penta.members.map((m) => m.avatar.token).join('|')]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const entries = await Promise.all(
        penta.members.map(async (oneMember) => {
          const kundli = await getMemberKundli(oneMember.avatar);
          return [oneMember.avatar.token, kundli];
        })
      );
      if (cancelled) return;
      setKundlis(Object.fromEntries(entries));
    };

    run();
    return () => { cancelled = true; };
  }, [
    penta.token,
    penta.members.map((m) => `${m.avatar.token}:${m.avatar?.dob}:${m.avatar?.city_id}:${m.avatar?.aggr?.city?.location}`).join('|'),
  ]);

  // Seed genders from avatar.gender; for 2 people keep them opposite when possible.
  useEffect(() => {
    if (!showCompat) {
      setGenders({});
      return;
    }
    const [tokenA, tokenB] = memberTokens;
    const rawA = normalizeGender(penta.members[0]?.avatar?.gender);
    const rawB = normalizeGender(penta.members[1]?.avatar?.gender);

    let nextA = rawA;
    let nextB = rawB;

    if (nextA && !nextB) nextB = oppositeGender(nextA);
    else if (nextB && !nextA) nextA = oppositeGender(nextB);
    else if (nextA && nextB && nextA === nextB) nextB = oppositeGender(nextA);

    setGenders({
      [tokenA]: nextA || null,
      [tokenB]: nextB || null,
    });
  }, [showCompat, memberTokens.join('|'), penta.members[0]?.avatar?.gender, penta.members[1]?.avatar?.gender]);

  const setPairGender = (token, gender) => {
    if (!showCompat) return;
    const [tokenA, tokenB] = memberTokens;
    const other = token === tokenA ? tokenB : tokenA;
    setGenders({
      [token]: gender,
      [other]: oppositeGender(gender),
    });
  };

  const memberA = showCompat ? penta.members[0] : null;
  const memberB = showCompat ? penta.members[1] : null;

  return (
    <>
    <div className="x_edu_content_title x_edu_content_title--group x_edu_content_title--sticky">
      <div className="x_edu_content_h x_edu_content_h--2">
        {$ui('popinfo_jyotish_title')}
      </div>
    </div>
    <div className="x_edu_content x_edu_content--penta x_edu_content--penta_jyotish _f  _fgap2 _fgrow _f100">
      <div className="x_edu_content_inner">

        <div className="x_edu_penta_persons">
          {penta.members.map((oneMember) => {
            const token = oneMember.avatar.token;
            const gender = genders[token] || null;
            return (
              <div
                key={`jyotish_head_${token}`}
                className="x_edu_penta_oneperson"
                style={{ flexBasis: compareColumnWidth }}
              >
                <div className="x_edu_h2 x_edu_h2--sticky">
                  <span className="x_ava_inline0">
                    <span className="x_ava_inline" style={{ display: 'inline-flex' }}>
                      <Avatar style={{ fontSize: '1em' }} avatar={oneMember.avatar} />
                    </span>
                    <span className={`x_ava_inline_name x_ava_inline_name--${oneMember.avatar.design.type.toLowerCase()}`}>
                      <AvatarName avatar={oneMember.avatar} />
                    </span>
                  </span>
                  {showCompat && (
                    <JyotishGenderToggle
                      gender={gender}
                      onChange={(next) => setPairGender(token, next)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="_fv _fgap1">
          {JYOTISH_SUMMARY_SECTIONS.map((section) => (
            <React.Fragment key={`jyotish_${penta.token}_${section}`}>
              <div className="x_edu_penta_persons">
                {penta.members.map((oneMember) => {
                  const kundli = kundlis[oneMember.avatar.token];
                  return (
                    <div
                      className="x_edu_penta_oneperson _fgap1"
                      key={`${section}_${oneMember.avatar.token}`}
                      style={{ flexBasis: compareColumnWidth }}
                    >
                      {kundli ? (
                        <JyotishSummary
                          kundli={kundli}
                          only={section}
                          itemClassName={PHS_ITEM_CLASS}
                        />
                      ) : (
                        <div className={PHS_ITEM_CLASS} />
                      )}
                    </div>
                  );
                })}
              </div>

              {showCompat && COMPAT_SECTIONS.includes(section) && (
                <JyotishCompat
                  section={section}
                  kundliA={kundlis[memberA.avatar.token]}
                  kundliB={kundlis[memberB.avatar.token]}
                  genderA={genders[memberA.avatar.token]}
                  genderB={genders[memberB.avatar.token]}
                  onGenderAChange={(next) => setPairGender(memberA.avatar.token, next)}
                  onGenderBChange={(next) => setPairGender(memberB.avatar.token, next)}
                />
              )}
            </React.Fragment>
          ))}
        </div>

      </div>
    </div>
    </>
  );
});
